/**
 * The two locked label vocabularies — copied verbatim from `server/amb_extract.py` (must stay in
 * lockstep with `engine.amb`, per that file's own comment). These are the ONLY two things a model
 * is ever allowed to judge; every dollar figure comes from the engine, never the model or this UI.
 */
export const CONDITIONS = ["renovated", "updated", "average", "dated", "as_is"] as const;
export type Condition = (typeof CONDITIONS)[number];

/**
 * THE LADDER'S DEFAULT, AND WHY IT NEEDS A FUNCTION (2026-08-13, found on a live Cotality-only
 * report: "when in cotality only data, it is marking every comp as renovated").
 *
 * `CONDITIONS` is ordered BEST-TO-WORST because the sort in `ConfirmGrid` reads its index as the
 * ladder rank. That ordering put "renovated" at index 0 — and an HTML `<select>` with no matching
 * option DISPLAYS ITS FIRST OPTION. Cotality's `comparables[]` carries no condition at all
 * (`cotality.COMP_FIELD_MAP` deliberately maps none: condition is a judgment, not a record), so on
 * a no-MLS build every comp arrived with `condition === undefined`, every dropdown rendered
 * "Renovated", and the agent was shown a two-step-superior rating on the single largest lever on
 * the screen — `PARAMS["condition_step"]` is 5% of each comp's OWN sale price per ladder step.
 *
 * The engine never believed it: `engine.amb` reads `c.get("condition", "average")` in both
 * `_lines` and `compute`, so the arithmetic and the PDF's condition table said Average the whole
 * time. That is the actual defect — the SCREEN and the DELIVERABLE disagreed, and the screen was
 * the one the agent was asked to approve. A silent disagreement between what a human confirms and
 * what the engine computes is worse than either value being wrong on its own.
 *
 * So: one function, matching the Python default exactly, applied wherever a condition is displayed
 * or a comp enters state. Absent, blank, or off-ladder all resolve to "average". KEEP IN LOCKSTEP
 * with `engine.amb`'s `.get("condition", "average")` — if that default ever moves, this moves with
 * it, and `validate_comp`'s ladder check is the backstop that catches a drift between them.
 */
export const DEFAULT_CONDITION: Condition = "average";

export function normalizeCondition(value: unknown): Condition {
  const v = String(value ?? "").trim();
  return (CONDITIONS as readonly string[]).includes(v) ? (v as Condition) : DEFAULT_CONDITION;
}

export const SALE_TYPES_KEEP = ["standard", "as_is_open_market", "investor_flip", "agent_owned"] as const;
export const SALE_TYPES_DROP = [
  "reo",
  "foreclosure",
  "short_sale",
  "auction",
  "estate_no_price",
  "family_transfer",
  "sheriff_sale",
] as const;
export const SALE_TYPES = [...SALE_TYPES_KEEP, ...SALE_TYPES_DROP] as const;
export type SaleType = (typeof SALE_TYPES)[number];

export const CONDITION_LABELS: Record<Condition, string> = {
  renovated: "Renovated",
  updated: "Updated",
  average: "Average",
  dated: "Dated",
  as_is: "As-is",
};

export const SALE_TYPE_LABELS: Record<string, string> = {
  standard: "Standard (arm's-length)",
  as_is_open_market: "As-is, open market",
  investor_flip: "Investor flip",
  agent_owned: "Agent-owned",
  reo: "REO — excluded",
  foreclosure: "Foreclosure — excluded",
  short_sale: "Short sale — excluded",
  auction: "Auction — excluded",
  estate_no_price: "Estate, no arm's-length price — excluded",
  family_transfer: "Family transfer — excluded",
  sheriff_sale: "Sheriff sale — excluded",
};

/**
 * The report tier — controls how much narrative work runs (VI/board doc §5 "AMB vs CMA: confirm
 * both report tiers are reachable"). Names/values copied verbatim from `server/amb_report.py`'s
 * `JobIn.tier` comment (`"cma_calc" | "cma_ai" | "amb"`) — not invented here.
 */
export const REPORT_TIERS = ["cma_calc", "cma_ai", "amb"] as const;
export type ReportTier = (typeof REPORT_TIERS)[number];

export const REPORT_TIER_LABELS: Record<ReportTier, string> = {
  cma_calc: "CMA — calculation only",
  cma_ai: "CMA — with AI narrative",
  amb: "Advanced Market Brief (AMB)",
};

/** A tier named in the address (`/cma?tier=cma_calc`), or null. The New brief dialog sends the
 *  report the person chose so the builder honors it rather than asking again (2026-09-25). Anything
 *  that is not one of the three is ignored, never guessed at. */
export function tierFromParam(value: string | null | undefined): ReportTier | null {
  return (REPORT_TIERS as readonly string[]).includes(value ?? "") ? (value as ReportTier) : null;
}

export const REPORT_TIER_HINTS: Record<ReportTier, string> = {
  cma_calc: "The adjusted-comp grid and the number, no AI narrative.",
  cma_ai: "The CMA grid plus a shorter AI-written narrative.",
  amb: "The full Advanced Market Brief — deepest narrative, every section.",
};

/**
 * The 5 named narrative voices (board doc `06_OWNER_DIRECTION_editable-personas-scope.md` §3 —
 * "Item B", owner-final names/ids/descriptors, copied verbatim). `house_blend` is the default per
 * that doc.
 *
 * ALL FIVE ARE LIVE (corrected 2026-07-29). This comment used to say the backend took no
 * `persona` parameter, and `PersonaPicker` disabled four of the five voices on that basis. It was
 * wrong: `server/amb_build.ai_narrative` has taken `persona` since Session 08, resolves it through
 * `amb_narrative_spec.resolve_persona`, and feeds a distinct multi-hundred-word voice body into
 * the system prompt for each id (`amb_narrative_spec.PERSONAS`). The ids below match that server
 * registry exactly, and `CmaAmbFlow` has been posting a real id in `meta.persona` the whole time —
 * so the voices were changing the prose while the UI told the user they did nothing.
 *
 * THE FLAGSHIP IS CALLED "The Blend" ON SCREEN (2026-08-05, owner instruction). The server renamed
 * it (`amb_narrative_spec.PERSONAS["house_blend"]["name"]`) and this table did not, and THIS table
 * is what the picker actually renders — so the rename shipped as a no-op for the only person it was
 * for. The `id` is untouched and stays `house_blend`: it is a wire contract (`meta.persona`, the
 * report filename slug, `resolve_persona`), and the server documents the id and the name as
 * deliberately allowed to disagree.
 *
 * THESE NAMES AND DESCRIPTORS ARE A HARDCODED SECOND COPY of a registry the server already owns,
 * and the rename above is what a second copy costs. `amb_narrative_spec.persona_choices()` exists
 * to be that single source ("the picker payload: what the client renders") but NOTHING calls it —
 * there is no route serving it and no `api.ts` function fetching it, so there is nothing to read
 * from today. Wiring it up is a follow-up, not this change. Until then: the five `descriptor`
 * strings below are ALSO out of step with the server's, which is the same drift one step behind.
 */
export const PERSONAS = [
  { id: "house_blend", name: "The Blend", descriptor: "The balanced default — a measured mix of the other four." },
  { id: "strategist", name: "The Strategist", descriptor: "Data-forward, pricing-strategy framing — the \"here's the play\" analyst voice." },
  { id: "advisor", name: "The Advisor", descriptor: "Measured, trusted-counsel tone — balanced, reassuring, fiduciary." },
  { id: "marketer", name: "The Marketer", descriptor: "Marketing-forward, benefits-led — names the home and the lifestyle (within the law)." },
  { id: "local_expert", name: "The Local Expert", descriptor: "Neighborhood-grounded — leans on place, subdivision, area facts." },
] as const;
export type PersonaId = (typeof PERSONAS)[number]["id"];
export const DEFAULT_PERSONA: PersonaId = "house_blend";

/**
 * Every stage token the backend can emit, mapped to a human label. Hoisted here (UI-050 part 2)
 * from `NarrativeReportScreen.tsx`, where UI-100 first introduced it — the extract progress panel
 * and the build progress panel must not drift into two vocabularies for the same server tokens.
 *
 * UI-100 (the defect that created this map): a licensed professional once saw the raw word `halt`
 * because a stage token was printed straight through. The fallback below is a neutral human phrase,
 * never the token.
 */
export const STAGE_LABEL: Record<string, string> = {
  queued: "Queued",
  running: "Building your report",
  read: "Reading your file",
  ocr: "Scanned pages — running text recognition (slower)",
  ai_extract: "Transcribing the subject and comparables",
  extract: "Reading your evidence",
  review_ready: "Ready for your review",
  compute: "Computing the value",
  "compute+draft": "Computing the value and drafting the narrative",
  draft: "Drafting the narrative",
  qa: "Quality-checking every figure against its source",
  halt: "Waiting on one more thing from you",
  done: "Done",
  failed: "Failed",
  needs_input: "One more thing needed",
};

export const STAGE_LABEL_FALLBACK = "Working on it";

export function stageLabel(stage: string | null | undefined): string {
  return (stage && STAGE_LABEL[stage]) || STAGE_LABEL_FALLBACK;
}

/**
 * The extract-side stage list, in the order the server walks it — ported verbatim from the apex
 * app's own `AMB_XSTAGES` (`src/server/web/index.html`), which has been in front of real users far
 * longer than `/next` has. Labels come from `STAGE_LABEL` above so the two surfaces cannot drift.
 *
 * `ocr` is CONDITIONAL: a text-native PDF never emits it. It is still listed, because a user
 * watching a scanned file needs to understand why the wait got longer — the panel marks it
 * "only if your file is scanned" rather than pretending it always runs.
 */
export const EXTRACT_STAGES = ["read", "ocr", "ai_extract", "review_ready"] as const;
export type ExtractStage = (typeof EXTRACT_STAGES)[number];

/**
 * Listing status, mirroring `engine/listing_status.py` exactly. Kept in lockstep with the engine
 * the same way CONDITIONS and SALE_TYPES are: a second spelling of "pending" on this side would
 * put a home on the wrong side of the valuation.
 *
 * There are THREE states, not two. Treating "not competing" as "closed" is what allowed an
 * `Active Contingent` or `Withdrawn` row — matching neither set — to be priced as a completed
 * sale. See the engine module for the full account.
 *
 * A record with no status at all is a CLOSED sale — every comp predating the field is one by
 * construction, because the extractor only ever asked for closed comps.
 */
export const CLOSED_STATUSES = ["closed", "sold"] as const;

export const COMPETING_STATUSES = [
  "active",
  "pending",
  "active_option_contract",
  "active_contingent", // under contract, contingency outstanding — NOT a sale
  "active_kick_out", // under contract with a kick-out clause — NOT a sale
  "coming_soon",
] as const;

/** Off the market without selling. Neither a sale nor competition. */
export const OFF_MARKET_STATUSES = ["withdrawn", "expired", "cancelled", "temporarily_off_market"] as const;

const KNOWN_STATUSES: readonly string[] = [
  ...CLOSED_STATUSES,
  ...COMPETING_STATUSES,
  ...OFF_MARKET_STATUSES,
];

/** Mirrors `engine.listing_status.normalize` — including its "unrecognized token passes
 * through unchanged" rule, which is what keeps the closed default safe for saved jobs. */
export function normalizeStatus(status?: string | null): string {
  const raw = String(status ?? "").trim().toLowerCase();
  if (!raw) return "closed";
  // The character class must match Python's `_SEPARATORS` in engine/listing_status.py EXACTLY,
  // including the Unicode dash block U+2010–U+2015 (hyphen, non-breaking hyphen, figure dash,
  // en dash, em dash, horizontal bar). Omitting them here made "active–option" normalize on the
  // server and not on the client — the same row on two sides of the valuation. Pinned by
  // `test_status_vocabulary` on the Python side and CompetingListings.test.tsx on this one.
  const s = raw
    .replace(/[\s/\\\-‐-―.,:;()[\]]+/g, "_")
    .replace(/_+/g, "_")
    .replace(/^_|_$/g, "");
  if (!s) return "closed";
  const aliases: Record<string, string> = {
    close: "closed",
    closed_sale: "closed",
    active_option: "active_option_contract",
    option_contract: "active_option_contract",
    option_pending: "active_option_contract",
    under_contract: "pending",
    pending_sale: "pending",
    pending_taking_backups: "pending",
    pending_continue_to_show: "pending",
    contingent: "active_contingent",
    active_contingency: "active_contingent",
    active_kickout: "active_kick_out",
    kick_out: "active_kick_out",
    coming_soon_no_show: "coming_soon",
    withdrawn_sublisting: "withdrawn",
    canceled: "cancelled",
    temp_off_market: "temporarily_off_market",
    off_market: "temporarily_off_market",
  };
  return aliases[s] ?? s;
}

export function isCompetingListing(c: { status?: string | null }): boolean {
  return (COMPETING_STATUSES as readonly string[]).includes(normalizeStatus(c.status));
}

/**
 * True for a COMPLETED sale. Use this rather than `!isCompetingListing(...)`: a status that is
 * neither closed nor competing — withdrawn, expired, cancelled — fell through that negation and
 * was counted as a sale. An unrecognized token still reads as closed, matching the engine.
 */
export function isClosedSale(c: { status?: string | null }): boolean {
  const s = normalizeStatus(c.status);
  return (CLOSED_STATUSES as readonly string[]).includes(s) || !KNOWN_STATUSES.includes(s);
}

/**
 * THE TWO SELLER-CONCESSION THRESHOLDS, copied from `engine.amb` (`CONCESSION_REVIEW_PCT`,
 * `CONCESSION_BLOCK_PCT` at amb.py:310-311) — the engine is authoritative and re-checks both on
 * submit; these exist so the review screen can EXPLAIN what the engine is about to do while the
 * agent can still act on it.
 *
 * They live here rather than inline because there are now three places that need the same two
 * numbers (the grid's status badge, the comps-step warning, and the >10% block), and the 2026-07-30
 * inventory audit already flagged hand-maintained JS↔Python constant duplication as a portability
 * risk. One copy is a risk; three copies is three things to keep in lockstep.
 *
 * WHAT EACH ONE MEANS, from the engine's own docstring:
 *   > REVIEW (6%)  a SOURCE-extracted figure above this is `review_required` — and
 *                  `screen_concessions` DROPS a `review_required` comparable from the valuation
 *                  outright. An agent confirmation clears it.
 *   > BLOCK (10%)  an absolute implausibility bound. NO confirmation accepts a value here: it has
 *                  to be corrected first.
 */
export const CONCESSION_REVIEW_PCT = 0.06;
export const CONCESSION_BLOCK_PCT = 0.10;

/**
 * Has a HUMAN confirmed this comp's concession? Asked exactly the way the engine asks it
 * (`engine.amb.comp_concession_status`, amb.py:397-402):
 *
 *     human_confirmed=bool(c.get("seller_concession_source") == "human"
 *                          or c.get("concession_human_confirmed"))
 *
 * Both spellings, because the engine honours both and a client that checked only one would tell
 * the agent a comparable is unconfirmed while the valuation treats it as confirmed. Note what is
 * NOT in here: `concession_status`. That is the engine's OUTPUT, recomputed from these inputs on
 * every run, and reading it back as if it were an input is the defect this helper exists to end.
 */
export function isConcessionHumanConfirmed(c: {
  seller_concession_source?: string | null;
  concession_human_confirmed?: boolean | null;
}): boolean {
  return c.seller_concession_source === "human" || !!c.concession_human_confirmed;
}

/**
 * The concession status of a comp the AGENT has just typed a figure into — a deliberate mirror of
 * `engine.amb.concession_status(price, value, human_confirmed=True)` (amb.py:323-366), used only to
 * label the cell the instant it changes rather than to decide anything.
 *
 * Mirrored rather than guessed at, and mirrored in FULL, because each branch is a different
 * sentence to the agent and getting any of them wrong is a false claim on screen:
 *   - a cleared cell is `assumed_zero` — the source said nothing and the app assumes $0. A human
 *     deleting a number has not confirmed a figure, so this branch comes FIRST, exactly as it does
 *     in the engine (a None concession returns before `human_confirmed` is ever consulted).
 *   - a figure that is negative, at or above the sale price, or over 10% of it is `blocked`. A
 *     confirmation cannot buy past the absolute bound — the engine says so in as many words, and a
 *     "you confirmed this" badge over a figure the build will refuse would be the worst of both.
 *   - anything else in the 0–10% band is `human_confirmed`, which is what clears the 6% review
 *     threshold and keeps the comparable in the valuation.
 *
 * The ENGINE still recomputes all of this from `seller_concession_source`; nothing here is trusted
 * server-side. It is the badge, not the gate.
 */
export function concessionStatusAfterHumanEdit(
  price: number | null | undefined,
  value: number | null,
): string {
  if (value == null) return "assumed_zero";
  const p = typeof price === "number" && Number.isFinite(price) ? price : null;
  if (!Number.isFinite(value) || value < 0 || p === null || p <= 0 || value >= p) return "blocked";
  if (value / p > CONCESSION_BLOCK_PCT) return "blocked";
  return "human_confirmed";
}

/**
 * ONE DROPZONE, TWO KINDS OF FILE (2026-07-30).
 *
 * The agent drops everything they have: an MLS comps export, an old listing, a CAD sheet, and a
 * photo of the house. Asking them to sort that into separate controls is friction over something the
 * computer can see for itself.
 *
 * The rule is an ALLOW-LIST of image types, not "anything unexpected must be the photo". A positive
 * test is the safe direction: the negative one would route a corrupt spreadsheet, or a `.doc` nobody
 * anticipated, into the photo slot and render a broken hero image on a client deliverable.
 *
 * SUPPORTED is what the server's validator actually accepts (`amb_report.py`:
 * "must be a valid PNG, JPEG, GIF, or WEBP image of 6 MB or less"). UNSUPPORTED is listed
 * separately and deliberately: HEIC is what an iPhone shoots by default, so it is the single most
 * likely photo an agent will hand us, and a clear message here beats a 400 from the server after
 * the upload.
 */
export const SUPPORTED_IMAGE_EXTENSIONS = ["jpg", "jpeg", "png", "gif", "webp"] as const;
export const UNSUPPORTED_IMAGE_EXTENSIONS = ["heic", "heif", "avif", "bmp", "tif", "tiff", "svg"] as const;

/** The server's own ceiling. Checked here so an oversized photo is caught before the round trip. */
export const MAX_SUBJECT_PHOTO_BYTES = 6 * 1024 * 1024;

export type UploadKind = "document" | "photo" | "photo-unsupported" | "photo-too-large";

function extensionOf(name: string): string {
  const i = name.lastIndexOf(".");
  return i === -1 ? "" : name.slice(i + 1).toLowerCase();
}

/**
 * What this file is for. Classified on extension AND the browser's MIME type, because a file saved
 * without an extension still usually carries `image/*`, and a `.jpg` that is really a PDF does not.
 */
export function classifyUpload(file: { name: string; type?: string; size?: number }): UploadKind {
  const ext = extensionOf(file.name);
  const mime = (file.type || "").toLowerCase();
  const looksLikeImage = mime.startsWith("image/") ||
    (SUPPORTED_IMAGE_EXTENSIONS as readonly string[]).includes(ext) ||
    (UNSUPPORTED_IMAGE_EXTENSIONS as readonly string[]).includes(ext);
  if (!looksLikeImage) return "document";

  const supported = (SUPPORTED_IMAGE_EXTENSIONS as readonly string[]).includes(ext) ||
    ["image/jpeg", "image/jpg", "image/png", "image/gif", "image/webp"].includes(mime);
  if (!supported) return "photo-unsupported";
  if (typeof file.size === "number" && file.size > MAX_SUBJECT_PHOTO_BYTES) return "photo-too-large";
  return "photo";
}

/** The actionable half of an unsupported-photo message. HEIC gets its own, because the fix is two
 * taps on the phone the agent is holding. */
export function photoProblemHelp(kind: UploadKind, name: string): string {
  const ext = extensionOf(name).toUpperCase();
  if (kind === "photo-too-large") {
    return `${name} is larger than 6 MB. Most phones can share a smaller copy, or a screenshot of ` +
      "the photo will work.";
  }
  if (ext === "HEIC" || ext === "HEIF") {
    return `${name} is an iPhone HEIC photo, which we can't read yet. In Photos, tap Share → ` +
      "Options → Most Compatible to send it as a JPEG, or take a screenshot of it.";
  }
  return `${name} is a ${ext || "photo"} file, which we can't read yet. A JPEG, PNG, GIF or WEBP works.`;
}
